import { FC, Fragment, useMemo } from 'react';

import {
   BasicFacetTermV2,
   BasicFacetWithTermsV2,
   FacetFilterTracking,
   FilterRange,
   technicalProperties
} from '@ravago/shared/page-data/models/elastic';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';

import { FacetContentV2, FacetGroupStructureV2 } from '../../../models/facet-group.model';
import {
   FACET_SORT_ORDER,
   RANGE_FACET_SORT_ORDER,
   doesFacetContainSearchValue,
   mergeNestedTerms,
   COLLECTION_FACET_SORT_ORDER
} from '../../../utils/facet-utils';

import { FacetTermItemV2 } from './facet-term-item-v2';
import { RangeChangeEventV2 } from './facet-term-v2';

interface Props {
   language: string;
   searchValue: string;
   isChild?: boolean;
   isCollectionGroup?: boolean;
   allNestedTermsOfParent?: Array<BasicFacetTermV2>;
   parentTerm?: string;
   isParentActive?: boolean;
   keepSortingOrder?: boolean;
   activeFacet?: BasicFacetWithTermsV2;
   activeNestedFacet?: BasicFacetWithTermsV2;
   facetGroupStructure: FacetGroupStructureV2;
   onFacetChange: (value: BasicFacetWithTermsV2[], switchToList?: boolean) => void;
   onFacetClicked: (facetFilter: FacetFilterTracking) => void;
}

export const FacetGroupTermListV2: FC<Props> = ({
   language,
   searchValue,
   isChild,
   isCollectionGroup,
   allNestedTermsOfParent,
   parentTerm,
   isParentActive,
   keepSortingOrder,
   activeFacet,
   facetGroupStructure,
   onFacetChange,
   onFacetClicked,
   activeNestedFacet
}) => {
   const visibleFacets = useMemo(() => {
      const filteredFacets = facetGroupStructure.facets.filter((facetContent) => doesFacetContainSearchValue(searchValue, facetContent));
      if (keepSortingOrder) {
         return filteredFacets;
      }
      if (isCollectionGroup) {
         // Collection-group top-level items (e.g. Technical Properties) sort by availability only;
         // selected items must NOT jump to the top
         return filteredFacets.sort(COLLECTION_FACET_SORT_ORDER);
      }
      return facetGroupStructure.facets.some((facet) => facet.range)
         ? filteredFacets.sort(RANGE_FACET_SORT_ORDER(activeFacet, activeNestedFacet))
         : filteredFacets.sort(FACET_SORT_ORDER(activeFacet, activeNestedFacet));
   }, [searchValue, facetGroupStructure, activeFacet, activeNestedFacet, keepSortingOrder, isCollectionGroup]);

   const collectionGroupChange = (facetGroupContent: FacetContentV2, checked: boolean, range?: FilterRange, switchToList?: boolean) => {
      const changes: BasicFacetWithTermsV2[] = [];
      if (checked) {
         if (facetGroupContent.nestedFacetGroups) {
            // Select all nested facets
            const allNestedTerms = facetGroupContent.nestedFacetGroups
               .flatMap((nestedFacetGroup) => nestedFacetGroup.facets)
               .map((nestedFacetGroup) => ({ key: nestedFacetGroup.key, parentKey: facetGroupContent.key }));

            changes.push({
               facetName: facetGroupContent.nestedFacetGroups[0].identifier.key,
               terms: mergeNestedTerms(allNestedTerms, facetGroupContent.key, activeNestedFacet?.terms)
            });
         } else {
            // Select only the main facet
            const newTerm: BasicFacetTermV2 = { key: facetGroupContent.key, parentKey: parentTerm, ...(range ? { range } : {}) };

            changes.push({ facetName: facetGroupContent.key, terms: [newTerm] });
         }
      } else if (activeNestedFacet?.terms.some((term) => term.parentKey === facetGroupContent.key)) {
         // Deselect all nested facets of this parent
         changes.push({
            facetName: activeNestedFacet.facetName,
            terms: mergeNestedTerms([], facetGroupContent.key, activeNestedFacet.terms)
         });
      } else {
         // Deselect only the collection term/facet
         changes.push({ facetName: facetGroupContent.key, terms: [] });
      }
      if (changes.length) onFacetChange(changes, switchToList);
   };

   const onCheckedChange = (facetGroupContent: FacetContentV2, checked: boolean, range?: FilterRange) => {
      const changes: BasicFacetWithTermsV2[] = [];
      let switchToList: boolean | undefined;

      if (checked) {
         const COLLECTION_SUFFIX = '-collection';
         onFacetClicked({ facet: facetGroupStructure.identifier.key, term: facetGroupContent.key, range });
         switchToList =
            technicalProperties.includes(facetGroupContent.key.replace(COLLECTION_SUFFIX, '') as any) ||
            technicalProperties.includes(facetGroupStructure.identifier.key as any) ||
            undefined;
      }

      if (isCollectionGroup) {
         // Handle collection group change
         collectionGroupChange(facetGroupContent, checked, range, switchToList);
         return;
      }

      if (checked && activeNestedFacet) {
         // Remove nested terms related to this parent when checking the main facet
         changes.push({
            facetName: activeNestedFacet.facetName,
            terms: mergeNestedTerms([], facetGroupContent.key, activeNestedFacet.terms)
         });
      }

      if (checked) {
         // Select main facet
         const newTerm: BasicFacetTermV2 = { key: facetGroupContent.key, parentKey: parentTerm, ...(range ? { range } : {}) };

         if (!activeFacet) {
            changes.push({ facetName: facetGroupStructure.identifier.key, terms: [newTerm] });
         } else {
            changes.push({
               ...activeFacet,
               terms: [...activeFacet.terms.filter((activeFacetTerm: BasicFacetTermV2) => activeFacetTerm.key !== newTerm.key), newTerm]
            });
         }
      } else if (isChild && allNestedTermsOfParent && isParentActive) {
         // Deselect all nested facets of this parent
         changes.push({
            facetName: facetGroupStructure.identifier.key,
            terms: allNestedTermsOfParent.filter((term) => term.key !== facetGroupContent.key)
         });
      } else {
         // Deselect main facet
         if (!activeFacet) return;
         const activeFacetCopy = { ...activeFacet };
         activeFacetCopy.terms = activeFacetCopy.terms.filter((activeFacetTerm: BasicFacetTermV2) => activeFacetTerm.key !== facetGroupContent.key);
         changes.push(activeFacetCopy);
      }
      if (changes.length) onFacetChange(changes, switchToList);
   };

   const onChildFilterChange = (
      changedFilter: BasicFacetWithTermsV2[],
      parentTermForChild: string,
      nestedFacetGroupStructure: FacetGroupStructureV2,
      switchToList?: boolean
   ) => {
      if (!parentTermForChild) return;

      const changedChildTerms: BasicFacetTermV2[] = changedFilter.flatMap((filter: BasicFacetWithTermsV2) => filter.terms);
      const newActiveNestedFilter = {
         facetName: nestedFacetGroupStructure.identifier.key,
         terms: mergeNestedTerms(changedChildTerms, parentTermForChild, activeNestedFacet?.terms)
      };

      if (changedChildTerms.length === 0) {
         // No active children: remove parent and nested
         if (!activeFacet) {
            onFacetChange([newActiveNestedFilter], switchToList);
            return;
         }
         const activeParentFacet = {
            ...activeFacet,
            terms: activeFacet.terms.filter((t) => t.key !== parentTermForChild)
         };
         onFacetChange(!isCollectionGroup ? [activeParentFacet, newActiveNestedFilter] : [newActiveNestedFilter], switchToList);
         return;
      }

      const parentIsInactive = !activeFacet?.terms?.find((t) => t.key === parentTermForChild);

      if (parentIsInactive) {
         // Parent is inactive: add parent and update nested
         const emptyActiveNestedFilter = {
            facetName: nestedFacetGroupStructure.identifier.key,
            terms: mergeNestedTerms([], parentTermForChild, activeNestedFacet?.terms)
         };
         const newActiveParentFilter: BasicFacetWithTermsV2 = {
            facetName: facetGroupStructure.identifier.key,
            terms: [{ key: parentTermForChild }, ...(activeFacet?.terms ?? [])]
         };
         const parentFacet = facetGroupStructure.facets.find((facet) => facet.key === parentTermForChild);
         const allChildrenSelected =
            parentFacet &&
            parentFacet.nestedFacetGroups &&
            parentFacet.nestedFacetGroups.every((group) => group.facets.every((f) => changedChildTerms.flatMap((t) => t.key).includes(f.key)));

         // If all children are selected and not a collection group, only add the parent
         onFacetChange(
            !isCollectionGroup && allChildrenSelected ? [newActiveParentFilter, emptyActiveNestedFilter] : [newActiveNestedFilter],
            switchToList
         );
         return;
      }

      // Parent is active: remove parent and update nested
      if (activeFacet) {
         const newActiveParentFacet: BasicFacetWithTermsV2 = {
            ...activeFacet,
            terms: activeFacet.terms.filter((t) => t.key !== parentTermForChild)
         };

         onFacetChange([newActiveParentFacet, newActiveNestedFilter], switchToList);
      }
   };

   const onRangeChange = (term: string, event: RangeChangeEventV2) => {
      if (!activeFacet) return;
      let newActiveFacet = { ...activeFacet };
      const changedTerm = newActiveFacet.terms.find((filterTerm) => filterTerm.key === term);
      if (!changedTerm) return;

      if (isCollectionGroup) {
         newActiveFacet = {
            facetName: term,
            terms: [{ key: term, range: { min: event.min, max: event.max } }]
         };
      } else {
         changedTerm.range = {
            min: event.min,
            max: event.max
         };
      }

      onFacetClicked({ facet: facetGroupStructure.identifier.key, term, range: event });
      onFacetChange([newActiveFacet]);
   };

   return (
      <Box direction="column" position="relative">
         <Box
            direction="column"
            maxHeight={!isChild ? `${50 * 8 + 20}px` : undefined}
            spacing={!isChild ? { bottom: 'xl' } : undefined}
            overflowX="auto"
         >
            <List>
               {visibleFacets.map((facet) => {
                  const activeTerm: BasicFacetTermV2 | undefined = activeFacet?.terms.find((t) => t.key === facet.key);
                  const allPossibleNestedFacetsForThisParent: string[] | undefined =
                     facet.nestedFacetGroups?.flatMap((nested) => nested?.facets?.flatMap((group) => group?.key)) ?? [];
                  const activeNestedFacetForThisParent: BasicFacetWithTermsV2 | undefined = activeNestedFacet?.facetName
                     ? {
                          facetName: activeNestedFacet?.facetName,
                          terms: activeNestedFacet?.terms.filter((t) => allPossibleNestedFacetsForThisParent.includes(t.key)) || []
                       }
                     : undefined;

                  return (
                     <Fragment key={facet.key}>
                        <FacetTermItemV2
                           searchValue={searchValue}
                           facetContent={facet}
                           activeTerm={activeTerm}
                           activeNestedFacetForThisParent={activeNestedFacetForThisParent}
                           isParentActive={isParentActive}
                           onCheckedChange={(checked) => onCheckedChange(facet, checked, facet.range)}
                           onRangeChange={(event) => onRangeChange(facet.key, event)}
                           renderChild={(parentActive) =>
                              facet.nestedFacetGroups?.map((nestedFacet) => (
                                 <Box key={`${nestedFacet.identifier.key}-${facet.key}`} margin={{ left: 'md' }}>
                                    <FacetGroupTermListV2
                                       language={language}
                                       keepSortingOrder={facet.keepFacetOrder}
                                       searchValue={searchValue}
                                       parentTerm={facet.key}
                                       isChild
                                       allNestedTermsOfParent={facet.nestedFacetGroups
                                          ?.flatMap((nestedFacetGroup) => nestedFacetGroup.facets)
                                          ?.map((nestedFacetGroup) => ({ key: nestedFacetGroup.key, parentKey: facet.key }))}
                                       isParentActive={parentActive}
                                       activeFacet={activeNestedFacetForThisParent}
                                       facetGroupStructure={nestedFacet}
                                       onFacetChange={(e, switchToList) => onChildFilterChange(e, facet.key, nestedFacet, switchToList)}
                                       onFacetClicked={onFacetClicked}
                                    />
                                 </Box>
                              ))
                           }
                        />
                     </Fragment>
                  );
               })}
            </List>
         </Box>

         {!isChild && <Box position="absolute" width="100%" height="50px" bottom="0" left="0" backgroundColor="gradient-fade-out-white" />}
      </Box>
   );
};
